import { useLayoutEffect, type RefObject } from 'react'
// 고르는 화면(서비스 홈·더보기)만. 자주 드나드는 업무 목록은 즉시 보여준다(root.css qsc-fade-in).
const ENTRY_PATHS = new Set(['/qsc/home', '/prm/home', '/qa/home', '/more'])
// 바깥쪽 요소만: 카드 안 버튼(Button 도 shadow-card)·헤더 안 요소가 따로 또 움직이지 않게 한다.
const TARGETS =
  '[data-enter]:not([data-enter] [data-enter]), .shadow-card:not(.shadow-card .shadow-card):not([data-enter] .shadow-card)'
const STAGGER_MS = 60
const MAX_STAGGER = 10
const ENTER_WINDOW_MS = 1400

/**
 * 서비스 홈·더보기에 들어올 때 배너·메뉴·카드를 홈처럼 하나씩 띄운다(CSS: root.css qsc-page-enter).
 * 늦게 그려지는 카드(lazy 페이지·데이터 로딩)도 ENTER_WINDOW_MS 안이면 같은 애니메이션을 탄다.
 * 홈(/home)은 useHomeMotion 이 따로 처리한다.
 */
export function usePageEnter(mainRef: RefObject<HTMLElement | null>, pathname: string) {
  useLayoutEffect(() => {
    const main = mainRef.current
    if (!main || pathname === '/home' || !ENTRY_PATHS.has(pathname)) return undefined
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined

    // 새로 그려진 요소들끼리 DOM 순서대로 지연을 준다.
    const assign = () => {
      let i = 0
      main.querySelectorAll<HTMLElement>(TARGETS).forEach((el) => {
        if (el.dataset.enterAssigned) return
        el.dataset.enterAssigned = ''
        el.style.setProperty('--enter-delay', `${Math.min(i++, MAX_STAGGER) * STAGGER_MS}ms`)
      })
    }
    assign()
    main.dataset.entering = ''
    const observer = new MutationObserver(assign)
    observer.observe(main, { childList: true, subtree: true })

    // data-enter-assigned 는 남겨 둔다. 지우면 목록용 fade 애니메이션이 다시 걸려 한 번 더 깜빡인다.
    const finish = () => {
      observer.disconnect()
      delete main.dataset.entering
    }
    const timer = window.setTimeout(finish, ENTER_WINDOW_MS)
    return () => {
      window.clearTimeout(timer)
      finish()
    }
  }, [mainRef, pathname])
}
